import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../utils/api';
import { PricingPlan, SimpleFeature } from '../../types';
import { useViewPreference } from '../../hooks/useViewPreference';
import {
  CheckIcon,
  XMarkIcon,
  PlusIcon,
  TrashIcon,
  Squares2X2Icon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import { StripeIndicator } from '../../components/StripeIndicator';
import { ToastContainer } from '../../components/Toast';
import { useToast } from '../../hooks/useToast';

// Currency symbol mapping
const getCurrencySymbol = (currency: string): string => {
  const symbols = {
    GBP: '£',
    USD: '$',
    EUR: '€',
  };
  return symbols[currency as keyof typeof symbols] || currency;
};

interface CreatePlanForm {
  id: string;
  name: string;
  displayName: string;
  description: string;
  priceAmount: string;
  pricePeriod: string;
  currency: string;
  isGlobal: boolean; // true for global plans, false for custom plans
  assignedOrgId: string; // only set for custom plans
  // Limits
  maxOrganisations: string;
  maxProjects: string;
  maxStreams: string;
  maxUsersMin: string;
  maxUsersMax: string;
  maxGuestLinks: string; // Move guestLinks to limits
  resolution: string;
  storageIncluded: string;
  storageCostPerGB: string;
  // Features
  features: {
    [key: string]: 'included' | 'chargeable' | 'disabled';
  };
}

export const PricingPlans: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [availableFeatures, setAvailableFeatures] = useState<SimpleFeature[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState<PricingPlan | null>(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);
  const [showCustomPlans, setShowCustomPlans] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const [planDeleteStatus, setPlanDeleteStatus] = useState<{
    [key: string]: { canDelete: boolean; reason?: string };
  }>({});
  // Stripe sync/cleanup actions moved to /ops/sync (Task 53c.4).
  const [viewMode, setViewMode] = useViewPreference('plans', 'grid');
  const [highlightedPlanId, setHighlightedPlanId] = useState<string | null>(null);

  // Toast notifications
  const { toasts, removeToast, showSuccess, showError } = useToast();

  // Add useEffect to scroll to form when it becomes visible
  useEffect(() => {
    if (showCreateForm) {
      // Use a small delay to ensure the form is rendered before scrolling
      const scrollTimeout = setTimeout(() => {
        const formElement = document.getElementById('pricing-plan-form');
        if (formElement) {
          // Get the element's position relative to the document
          const elementRect = formElement.getBoundingClientRect();
          const absoluteElementTop = elementRect.top + window.pageYOffset;

          // Account for sticky navbar height (64px = h-16 in Tailwind) plus some padding
          const navbarHeight = 64;
          const padding = 20; // Additional padding for better visual spacing
          const offsetPosition = absoluteElementTop - navbarHeight - padding;

          // Smooth scroll to the calculated position
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          });
        }
      }, 150); // Slightly increased delay to ensure proper rendering
      return () => clearTimeout(scrollTimeout);
    }
  }, [showCreateForm]);

  // State for organization names cache
  const [organizationNamesCache, setOrganizationNamesCache] = useState<{ [key: string]: string }>(
    {}
  );
  const [loadingOrgNames, setLoadingOrgNames] = useState<Set<string>>(new Set());

  // Helper function to get organization name from ID
  const getOrganizationName = (orgId: string): string => {
    // Return cached name if available
    if (organizationNamesCache[orgId]) {
      return organizationNamesCache[orgId];
    }

    // Show loading state while fetching
    if (loadingOrgNames.has(orgId)) {
      return 'Loading...';
    }

    // Start fetching organization name
    setLoadingOrgNames((prev) => new Set(prev).add(orgId));

    api
      .getOrganisation(orgId)
      .then((org) => {
        setOrganizationNamesCache((prev) => ({ ...prev, [orgId]: org.name }));
        setLoadingOrgNames((prev) => {
          const newSet = new Set(prev);
          newSet.delete(orgId);
          return newSet;
        });
      })
      .catch((error) => {
        console.error(`Failed to fetch organization ${orgId}:`, error);
        setOrganizationNamesCache((prev) => ({ ...prev, [orgId]: 'Unknown Organization' }));
        setLoadingOrgNames((prev) => {
          const newSet = new Set(prev);
          newSet.delete(orgId);
          return newSet;
        });
      });

    return 'Loading...';
  };

  // Separate plans into global and custom
  const globalPlans = plans.filter((plan) => plan.isGlobal ?? true);
  const customPlans = plans.filter((plan) => !(plan.isGlobal ?? true));

  const getDefaultFormData = (): CreatePlanForm => {
    const features: { [key: string]: 'included' | 'chargeable' | 'disabled' } = {};
    // Initialize features from API data - default to 'included'
    availableFeatures.forEach((feature) => {
      features[feature.name] = 'included';
    });

    return {
      id: '',
      name: '',
      displayName: '',
      description: '',
      priceAmount: '0',
      pricePeriod: 'monthly',
      currency: 'GBP',
      isGlobal: true, // default to global plan
      assignedOrgId: '',
      maxOrganisations: '1',
      maxProjects: '1',
      maxStreams: '1',
      maxUsersMin: '1',
      maxUsersMax: '10',
      maxGuestLinks: '0', // Move guestLinks to limits
      resolution: 'up_to_1080p',
      storageIncluded: '0',
      storageCostPerGB: '0',
      features,
    };
  };

  const [formData, setFormData] = useState<CreatePlanForm>(getDefaultFormData());

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch both plans and features
        const [plansData, featuresData] = await Promise.all([
          api.getAllPlans(),
          api.getSimpleFeatures(),
        ]);

        setPlans(plansData);
        setAvailableFeatures(featuresData);

        // Check delete status for each plan
        const deleteStatus: { [key: string]: { canDelete: boolean; reason?: string } } = {};
        for (const plan of plansData) {
          try {
            const status = await api.canDeletePlan(plan.id);
            deleteStatus[plan.id] = status;
          } catch (error) {
            console.error(`Error checking delete status for plan ${plan.id}:`, error);
            deleteStatus[plan.id] = { canDelete: false, reason: 'Error checking delete status' };
          }
        }
        setPlanDeleteStatus(deleteStatus);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Effect to handle plan URL parameter for auto-highlighting
  useEffect(() => {
    if (!loading && plans.length > 0) {
      const planId = searchParams.get('plan');

      if (planId) {
        // Check if the plan exists in the loaded plans
        const foundPlan = plans.find((plan) => plan.id === planId);

        if (foundPlan) {
          // Highlight the plan
          setHighlightedPlanId(planId);

          // If the plan is a custom plan, auto-expand the custom plans section
          if (!(foundPlan.isGlobal ?? true)) {
            setShowCustomPlans(true);
          }

          // Scroll to the plan after a brief delay to ensure rendering is complete
          setTimeout(() => {
            const planElement = document.getElementById(`plan-${planId}`);
            if (planElement) {
              const elementRect = planElement.getBoundingClientRect();
              const absoluteElementTop = elementRect.top + window.pageYOffset;

              // Account for navbar height and padding
              const navbarHeight = 64;
              const padding = 20;
              const offsetPosition = absoluteElementTop - navbarHeight - padding;

              window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth',
              });
            }
          }, 200);

          // Clear the URL parameter after a few seconds to prevent repeated highlighting on refresh
          setTimeout(() => {
            const newSearchParams = new URLSearchParams(searchParams.toString());
            newSearchParams.delete('plan');
            setSearchParams(newSearchParams);
            setHighlightedPlanId(null);
          }, 5000);
        } else {
          // Plan not found - clear the parameter
          const newSearchParams = new URLSearchParams(searchParams.toString());
          newSearchParams.delete('plan');
          setSearchParams(newSearchParams);
        }
      }
    }
  }, [loading, plans, searchParams, setSearchParams]);

  const handleInputChange = (field: keyof CreatePlanForm, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFeatureChange = (
    featureKey: string,
    value: 'included' | 'chargeable' | 'disabled'
  ) => {
    setFormData((prev) => ({
      ...prev,
      features: {
        ...prev.features,
        [featureKey]: value,
      },
    }));
  };

  const handleEditPlan = (plan: PricingPlan) => {
    setEditingPlan(plan);
    // Populate form with existing plan data
    setFormData({
      id: plan.id,
      name: plan.name,
      displayName: plan.displayName || '',
      description: plan.description || '',
      priceAmount: String(typeof plan.price === 'number' ? plan.price : plan.price?.amount || 0),
      pricePeriod: typeof plan.price === 'object' ? plan.price.period : 'monthly',
      currency: typeof plan.price === 'object' ? plan.price.currency : 'GBP',
      isGlobal: plan.isGlobal ?? true,
      assignedOrgId: plan.assignedOrgId || '',
      maxOrganisations: String(plan.limits?.organisations || 1),
      maxProjects: String(plan.limits?.projects || 1),
      maxStreams: String(plan.limits?.streams || 1),
      maxUsersMin: String(typeof plan.limits?.users === 'object' ? plan.limits.users.min : 1),
      maxUsersMax: String(typeof plan.limits?.users === 'object' ? plan.limits.users.max : 10),
      maxGuestLinks: String(plan.limits?.guestLinks || 0), // Move guestLinks to limits
      resolution: plan.limits?.resolution || 'up_to_1080p',
      storageIncluded: String(plan.limits?.storage?.included || 0),
      storageCostPerGB: String(plan.limits?.storage?.costPerGB || 0),
      features: (() => {
        // Build features object from API data
        const features: { [key: string]: 'included' | 'chargeable' | 'disabled' } = {};
        availableFeatures.forEach((feature) => {
          const featureValue = plan.features?.[feature.name];
          // Convert boolean values to new format for backwards compatibility
          if (featureValue === true) {
            features[feature.name] = 'included';
          } else if (featureValue === false || featureValue === undefined) {
            features[feature.name] = 'disabled';
          } else {
            features[feature.name] = featureValue as 'included' | 'chargeable' | 'disabled';
          }
        });
        return features;
      })(),
    });
    setShowCreateForm(true);
  };

  const handleClonePlan = (plan: PricingPlan) => {
    setEditingPlan(null); // Clear editing mode for cloning
    // Create a new plan based on the existing one but with a new ID and name
    const clonedFormData = {
      id: `${plan.id}_clone_${Date.now()}`, // Generate unique ID
      name: `${plan.name} (Copy)`,
      displayName: plan.displayName ? `${plan.displayName} (Copy)` : '',
      description: plan.description || '',
      priceAmount: String(typeof plan.price === 'number' ? plan.price : plan.price?.amount || 0),
      pricePeriod: typeof plan.price === 'object' ? plan.price.period : 'monthly',
      currency: typeof plan.price === 'object' ? plan.price.currency : 'GBP',
      isGlobal: false, // Cloned plans default to custom
      assignedOrgId: '',
      maxOrganisations: String(plan.limits?.organisations || 1),
      maxProjects: String(plan.limits?.projects || 1),
      maxStreams: String(plan.limits?.streams || 1),
      maxUsersMin: String(typeof plan.limits?.users === 'object' ? plan.limits.users.min : 1),
      maxUsersMax: String(typeof plan.limits?.users === 'object' ? plan.limits.users.max : 10),
      maxGuestLinks: String(plan.limits?.guestLinks || 0), // Move guestLinks to limits for cloning too
      resolution: plan.limits?.resolution || 'up_to_1080p',
      storageIncluded: String(plan.limits?.storage?.included || 0),
      storageCostPerGB: String(plan.limits?.storage?.costPerGB || 0),
      features: (() => {
        // Build features object dynamically from API data
        const features: { [key: string]: 'included' | 'chargeable' | 'disabled' } = {};
        availableFeatures.forEach((feature) => {
          const featureValue = plan.features?.[feature.name];
          // Convert boolean values to new format for backwards compatibility
          if (featureValue === true) {
            features[feature.name] = 'included';
          } else if (featureValue === false || featureValue === undefined) {
            features[feature.name] = 'disabled';
          } else {
            features[feature.name] = featureValue as 'included' | 'chargeable' | 'disabled';
          }
        });
        return features;
      })(),
    };
    setFormData(clonedFormData);
    setShowCreateForm(true);
  };

  const handleDeletePlan = async (plan: PricingPlan) => {
    const deleteStatus = planDeleteStatus[plan.id];

    if (!deleteStatus?.canDelete) {
      const reason = deleteStatus?.reason || 'Cannot delete this plan';
      showError('Cannot Delete Plan', `Cannot delete plan "${plan.name}": ${reason}`);
      return;
    }

    const confirmMessage = `Are you sure you want to delete the plan "${plan.name}"? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setDeleteLoading(plan.id);

    try {
      await api.deletePlan(plan.id);

      // Remove the plan from the state
      setPlans((prev) => prev.filter((p) => p.id !== plan.id));

      // Remove the plan from delete status
      setPlanDeleteStatus((prev) => {
        const newStatus = { ...prev };
        delete newStatus[plan.id];
        return newStatus;
      });

      // Show success toast for 2 seconds
      showSuccess('Plan Deleted', `"${plan.name}" has been deleted successfully`, 2000);
    } catch (error) {
      console.error('Error deleting plan:', error);
      showError(
        'Delete Failed',
        `Failed to delete plan "${plan.name}": ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      setDeleteLoading(null);
    }
  };

  const resetForm = () => {
    setFormData(getDefaultFormData());
    setEditingPlan(null);
    setShowCreateForm(false);
    setCreateError(null);
    setCreateSuccess(null);
  };

  // Stripe sync/cleanup actions (sync, cleanup orphaned, purge products,
  // cleanup-without-IDs, find/cleanup duplicates) moved to /ops/sync in
  // Task 53c.4. They live as confirm-required rows in OpsActionList there.

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateLoading(true);
    setCreateError(null);
    setCreateSuccess(null);

    try {
      // Convert form data to API format
      const planData = {
        id: formData.id,
        name: formData.name,
        displayName: formData.displayName,
        description: formData.description,
        price: {
          amount: Number(formData.priceAmount) || 0,
          currency: formData.currency,
          period: formData.pricePeriod,
        },
        minimumTerm: {
          amount: 1,
          period: formData.pricePeriod,
        },
        limits: {
          organisations:
            formData.maxOrganisations === 'unlimited'
              ? 'unlimited'
              : Number(formData.maxOrganisations) || 1,
          projects:
            formData.maxProjects === 'unlimited' ? 'unlimited' : Number(formData.maxProjects) || 1,
          streams:
            formData.maxStreams === 'unlimited' ? 'unlimited' : Number(formData.maxStreams) || 1,
          users: {
            min: Number(formData.maxUsersMin) || 1,
            max:
              formData.maxUsersMax === 'unlimited'
                ? 'unlimited'
                : Number(formData.maxUsersMax) || 10,
          },
          guestLinks:
            formData.maxGuestLinks === 'unlimited'
              ? 'unlimited'
              : Number(formData.maxGuestLinks) || 0,
          resolution: formData.resolution as 'up_to_1080p' | 'up_to_4k',
          storage: {
            included: Number(formData.storageIncluded) || 0,
            costPerGB: Number(formData.storageCostPerGB) || 0,
          },
        },
        features: formData.features,
        isGlobal: formData.isGlobal,
        assignedOrgId: formData.isGlobal ? undefined : formData.assignedOrgId || undefined,
        active: true,
        sortOrder: 999, // Will be automatically assigned by backend
      } as Partial<PricingPlan>;

      let resultPlan: PricingPlan;

      if (editingPlan) {
        // Update existing plan
        resultPlan = await api.updatePlan(editingPlan.id, planData);
        // Update existing plan in the list
        setPlans((prev) => prev.map((p) => (p.id === editingPlan.id ? resultPlan : p)));
        setCreateSuccess(`Plan "${resultPlan.name}" updated successfully!`);
      } else {
        // Create new plan
        resultPlan = await api.createPlan(planData);
        // Add new plan to the list
        setPlans((prev) => [...prev, resultPlan]);
        setCreateSuccess(`Plan "${resultPlan.name}" created successfully!`);
      }

      resetForm();
    } catch (error) {
      setCreateError(error instanceof Error ? error.message : 'Failed to create plan');
    } finally {
      setCreateLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-base-100 shadow rounded-lg p-6">
        <div className="flex justify-between items-center p-2 rounded">
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-bold text-base-content mb-1">Pricing Plans</h1>
            <p className="text-base-content/70 hidden md:block">Available pricing plans</p>
          </div>
          <div className="flex items-center gap-2 flex-nowrap flex-shrink-0">
            {/* View Toggle */}
            <div className="flex items-center bg-base-200 rounded-lg p-1 flex-shrink-0">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-base-100 text-base-content shadow-sm'
                    : 'text-base-content/70 hover:text-base-content'
                }`}
                title="Grid view"
              >
                <Squares2X2Icon className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'table'
                    ? 'bg-base-100 text-base-content shadow-sm'
                    : 'text-base-content/70 hover:text-base-content'
                }`}
                title="Table view"
              >
                <TableCellsIcon className="h-4 w-4" />
              </button>
            </div>
            <button
              onClick={() => {
                resetForm();
                setShowCreateForm(true);
              }}
              disabled={showCreateForm}
              className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${showCreateForm ? 'bg-base-content/40 cursor-not-allowed' : 'bg-primary hover:bg-primary/80'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary whitespace-nowrap flex-shrink-0`}
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Create New Plan
            </button>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {createSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <CheckIcon className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">{createSuccess}</p>
            </div>
            <button
              onClick={() => setCreateSuccess(null)}
              className="ml-auto -mx-1.5 -my-1.5 bg-green-50 text-green-500 rounded-lg focus:ring-2 focus:ring-green-600 p-1.5 hover:bg-green-100"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {createError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <XMarkIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">{createError}</p>
            </div>
            <button
              onClick={() => setCreateError(null)}
              className="ml-auto -mx-1.5 -my-1.5 bg-red-50 text-red-500 rounded-lg focus:ring-2 focus:ring-red-600 p-1.5 hover:bg-red-100"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Create Plan Form */}
      {showCreateForm && (
        <div id="pricing-plan-form" className="bg-base-100 shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-base-content">
              {editingPlan ? 'Edit Pricing Plan' : 'Create New Pricing Plan'}
            </h2>
            <button onClick={resetForm} className="text-base-content/50 hover:text-base-content/70">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-base-content">Basic Information</h3>

                <div>
                  <label className="block text-sm font-medium text-base-content">Plan ID</label>
                  <input
                    type="text"
                    required
                    disabled={!!editingPlan}
                    className={`mt-1 block w-full border-base-300 rounded-md shadow-sm focus:ring-primary focus:border-primary ${
                      editingPlan ? 'bg-base-200 cursor-not-allowed' : ''
                    }`}
                    value={formData.id}
                    onChange={(e) => handleInputChange('id', e.target.value)}
                    placeholder="e.g., custom_plan_1"
                  />
                  {editingPlan && (
                    <p className="mt-1 text-sm text-base-content/70">
                      Plan ID cannot be changed when editing
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-base-content">Plan Name</label>
                  <input
                    type="text"
                    required
                    className="mt-1 block w-full border-base-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="e.g., Custom Plan"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-base-content">
                    Display Name
                  </label>
                  <input
                    type="text"
                    className="mt-1 block w-full border-base-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    value={formData.displayName}
                    onChange={(e) => handleInputChange('displayName', e.target.value)}
                    placeholder="Leave empty to use Plan Name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-base-content">Description</label>
                  <textarea
                    rows={3}
                    className="mt-1 block w-full border-base-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Describe this pricing plan..."
                  />
                </div>

                {/* Plan Type - only show when creating, not when editing */}
                {!editingPlan && (
                  <div>
                    <label className="block text-sm font-medium text-base-content">Plan Type</label>
                    <div className="mt-1 space-y-2">
                      <div className="flex items-center">
                        <input
                          type="radio"
                          id="global"
                          name="planType"
                          className="h-4 w-4 text-primary focus:ring-primary border-base-300"
                          checked={formData.isGlobal}
                          onChange={() => handleInputChange('isGlobal', true)}
                        />
                        <label htmlFor="global" className="ml-2 text-sm text-base-content">
                          Global Plan (available to all organizations)
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="radio"
                          id="custom"
                          name="planType"
                          className="h-4 w-4 text-primary focus:ring-primary border-base-300"
                          checked={!formData.isGlobal}
                          onChange={() => handleInputChange('isGlobal', false)}
                        />
                        <label htmlFor="custom" className="ml-2 text-sm text-base-content">
                          Custom Plan (organization-specific)
                        </label>
                      </div>
                    </div>
                    <p className="mt-1 text-sm text-base-content/70">
                      {formData.isGlobal
                        ? 'This plan will be available to all organizations when creating new accounts.'
                        : 'This plan can be assigned to specific organizations and will not appear in global plan listings.'}
                    </p>
                  </div>
                )}

                {/* Organization Assignment - only for custom plans */}
                {!formData.isGlobal && (
                  <div>
                    <label className="block text-sm font-medium text-base-content">
                      Assigned Organization
                    </label>
                    <input
                      type="text"
                      className="mt-1 block w-full border-base-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                      value={formData.assignedOrgId}
                      onChange={(e) => handleInputChange('assignedOrgId', e.target.value)}
                      placeholder="Organization ID (leave empty for unassigned)"
                    />
                    <p className="mt-1 text-sm text-base-content/70">
                      Optional: Assign this custom plan to a specific organization
                    </p>
                  </div>
                )}
              </div>

              {/* Pricing */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-base-content">Pricing</h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-base-content">
                      Price Amount
                    </label>
                    <input
                      type="text"
                      className="mt-1 block w-full border-base-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                      value={formData.priceAmount}
                      onChange={(e) => handleInputChange('priceAmount', e.target.value)}
                      placeholder="0.00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-base-content">
                      Billing Period
                    </label>
                    <select
                      className="mt-1 block w-full border-base-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                      value={formData.pricePeriod}
                      onChange={(e) => handleInputChange('pricePeriod', e.target.value)}
                    >
                      <option value="daily">Daily</option>
                      <option value="monthly">Monthly</option>
                      <option value="annually">Annually</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-base-content">Currency</label>
                  <select
                    className="mt-1 block w-full border-base-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    value={formData.currency}
                    onChange={(e) => handleInputChange('currency', e.target.value)}
                  >
                    <option value="GBP">GBP (£)</option>
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Limits */}
            <div>
              <h3 className="text-lg font-medium text-base-content mb-4">Limits</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-base-content">
                    Max Organisations
                  </label>
                  <input
                    type="text"
                    className="mt-1 block w-full border-base-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    value={formData.maxOrganisations}
                    onChange={(e) => handleInputChange('maxOrganisations', e.target.value)}
                    placeholder="Number or 'unlimited'"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-base-content">
                    Max Projects
                  </label>
                  <input
                    type="text"
                    className="mt-1 block w-full border-base-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    value={formData.maxProjects}
                    onChange={(e) => handleInputChange('maxProjects', e.target.value)}
                    placeholder="Number or 'unlimited'"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-base-content">Max Streams</label>
                  <input
                    type="text"
                    className="mt-1 block w-full border-base-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    value={formData.maxStreams}
                    onChange={(e) => handleInputChange('maxStreams', e.target.value)}
                    placeholder="Number or 'unlimited'"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-base-content">Min Users</label>
                  <input
                    type="text"
                    className="mt-1 block w-full border-base-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    value={formData.maxUsersMin}
                    onChange={(e) => handleInputChange('maxUsersMin', e.target.value)}
                    placeholder="1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-base-content">Max Users</label>
                  <input
                    type="text"
                    className="mt-1 block w-full border-base-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    value={formData.maxUsersMax}
                    onChange={(e) => handleInputChange('maxUsersMax', e.target.value)}
                    placeholder="Number or 'unlimited'"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-base-content">Resolution</label>
                  <select
                    className="mt-1 block w-full border-base-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    value={formData.resolution}
                    onChange={(e) => handleInputChange('resolution', e.target.value)}
                  >
                    <option value="up_to_1080p">Up to 1080p</option>
                    <option value="up_to_4k">Up to 4K</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-base-content">
                    Storage Included (GB)
                  </label>
                  <input
                    type="text"
                    className="mt-1 block w-full border-base-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    value={formData.storageIncluded}
                    onChange={(e) => handleInputChange('storageIncluded', e.target.value)}
                    placeholder="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-base-content">
                    Storage Cost per GB (£)
                  </label>
                  <input
                    type="text"
                    className="mt-1 block w-full border-base-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    value={formData.storageCostPerGB}
                    onChange={(e) => handleInputChange('storageCostPerGB', e.target.value)}
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-base-content">
                    Max Guest Links
                  </label>
                  <input
                    type="text"
                    className="mt-1 block w-full border-base-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    value={formData.maxGuestLinks}
                    onChange={(e) => handleInputChange('maxGuestLinks', e.target.value)}
                    placeholder="Number or 'unlimited'"
                  />
                  <p className="mt-1 text-sm text-base-content/70">
                    Maximum number of guest links allowed
                  </p>
                </div>
              </div>
            </div>

            {/* Features */}
            <div>
              <h3 className="text-lg font-medium text-base-content mb-4">Features</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(formData.features).map(([featureKey, value]) => {
                  // Find the feature from availableFeatures to get display name
                  const feature = availableFeatures.find((f) => f.name === featureKey);
                  const displayName =
                    feature?.displayName ||
                    featureKey.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());

                  return (
                    <div key={featureKey} className="border border-base-300 rounded-lg p-4">
                      <div className="mb-3">
                        <label className="text-sm font-medium text-base-content">
                          {String(displayName)}
                        </label>
                        {feature?.description && (
                          <p className="text-xs text-base-content/70 mt-1">
                            {String(feature.description)}
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <input
                            type="radio"
                            id={`${featureKey}-included`}
                            name={featureKey}
                            value="included"
                            className="h-4 w-4 text-primary focus:ring-primary border-base-300"
                            checked={value === 'included'}
                            onChange={() => handleFeatureChange(featureKey, 'included')}
                          />
                          <label
                            htmlFor={`${featureKey}-included`}
                            className="ml-2 text-sm text-base-content"
                          >
                            Included in plan
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="radio"
                            id={`${featureKey}-chargeable`}
                            name={featureKey}
                            value="chargeable"
                            className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-base-300"
                            checked={value === 'chargeable'}
                            onChange={() => handleFeatureChange(featureKey, 'chargeable')}
                          />
                          <label
                            htmlFor={`${featureKey}-chargeable`}
                            className="ml-2 text-sm text-base-content"
                          >
                            Chargeable separately
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="radio"
                            id={`${featureKey}-disabled`}
                            name={featureKey}
                            value="disabled"
                            className="h-4 w-4 text-base-content/70 focus:ring-base-content/50 border-base-300"
                            checked={value === 'disabled'}
                            onChange={() => handleFeatureChange(featureKey, 'disabled')}
                          />
                          <label
                            htmlFor={`${featureKey}-disabled`}
                            className="ml-2 text-sm text-base-content"
                          >
                            Not available
                          </label>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border border-base-300 rounded-md shadow-sm text-sm font-medium text-base-content bg-base-100 hover:bg-base-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createLoading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createLoading
                  ? editingPlan
                    ? 'Updating...'
                    : 'Creating...'
                  : editingPlan
                    ? 'Update Plan'
                    : 'Create Plan'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Global Plans - Conditional rendering based on view mode */}
      {viewMode === 'grid' ? (
        // Grid View for Global Plans
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {globalPlans.map((plan) => (
            <div
              key={plan.id}
              id={`plan-${plan.id}`}
              className={`relative bg-base-100 rounded-lg shadow-md border-2 p-6 transition-all duration-500 ${
                highlightedPlanId === plan.id
                  ? 'border-yellow-400 ring-4 ring-yellow-200 shadow-lg bg-yellow-50'
                  : plan.name?.includes('pro') || plan.name?.includes('enterprise')
                    ? 'border-primary ring-2 ring-primary/30'
                    : 'border-base-300'
              }`}
            >
              {(plan.name?.includes('pro') || plan.name?.includes('enterprise')) && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-primary text-primary-content px-3 py-1 text-xs font-medium rounded-full">
                    Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-4">
                <div className="flex items-center justify-center mb-2 space-x-2">
                  <h3 className="text-2xl font-bold text-base-content">
                    {plan.name || 'Unnamed Plan'}
                  </h3>
                  <StripeIndicator
                    stripeProductId={plan.stripeProductId}
                    stripePriceId={plan.stripePriceId}
                  />
                </div>
                <div className="text-4xl font-bold text-base-content mb-1">
                  {getCurrencySymbol(
                    typeof plan.price === 'object' ? plan.price?.currency || 'GBP' : 'GBP'
                  )}
                  {typeof plan.price === 'number' ? plan.price : plan.price?.amount || 0}
                  <span className="text-lg text-base-content/70 font-normal">
                    /
                    {plan.billingCycle ||
                      (typeof plan.price === 'object' ? plan.price.period : 'month')}
                  </span>
                </div>
                <p className="text-base-content/70">
                  {plan.description || 'No description available'}
                </p>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-center text-sm">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                  <span>
                    Max Organisations:{' '}
                    {plan.limits?.organisations === 'unlimited'
                      ? 'Unlimited'
                      : plan.limits?.organisations || '1'}
                  </span>
                </div>
                <div className="flex items-center text-sm">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                  <span>
                    Max Projects:{' '}
                    {plan.limits?.projects === 'unlimited'
                      ? 'Unlimited'
                      : plan.limits?.projects || '1'}
                  </span>
                </div>
                <div className="flex items-center text-sm">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                  <span>
                    Max Streams:{' '}
                    {plan.limits?.streams === 'unlimited'
                      ? 'Unlimited'
                      : plan.limits?.streams || '1'}
                  </span>
                </div>
                <div className="flex items-center text-sm">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                  <span>
                    Max Users:{' '}
                    {plan.limits?.users?.max === 'unlimited'
                      ? 'Unlimited'
                      : `${plan.limits?.users?.min || 1}-${plan.limits?.users?.max || 10}`}
                  </span>
                </div>
                <div className="flex items-center text-sm">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                  <span>
                    Resolution:{' '}
                    {plan.limits?.resolution === 'up_to_4k' ? 'Up to 4K' : 'Up to 1080p'}
                  </span>
                </div>
                {plan.limits?.storage && (
                  <div className="flex items-center text-sm">
                    <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                    <span>
                      Storage: {plan.limits.storage.included}GB included
                      {plan.limits.storage.costPerGB > 0
                        ? ` + £${plan.limits.storage.costPerGB}/GB`
                        : ''}
                    </span>
                  </div>
                )}

                {plan.features && Object.keys(plan.features).length > 0 && (
                  <div className="pt-3 border-t border-base-300">
                    <p className="text-sm font-medium text-base-content mb-2">Features:</p>
                    <div className="grid grid-cols-2 gap-1">
                      {Object.entries(plan.features)
                        .filter(([, enabled]) => enabled === true) // Only show enabled features
                        .slice(0, 8) // Show max 8 features to avoid clutter
                        .map(([feature]) => (
                          <div key={feature} className="flex items-center text-xs">
                            <CheckIcon className="h-3 w-3 text-green-500 mr-1 flex-shrink-0" />
                            <span className="text-base-content truncate">
                              {feature
                                .replace(/([A-Z])/g, ' $1')
                                .replace(/^./, (str) => str.toUpperCase())}
                            </span>
                          </div>
                        ))}
                      {Object.entries(plan.features).filter(([, enabled]) => enabled === true)
                        .length > 8 && (
                        <div className="text-xs text-base-content/70 col-span-2">
                          +
                          {Object.entries(plan.features).filter(([, enabled]) => enabled === true)
                            .length - 8}{' '}
                          more features
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex space-x-2">
                <button
                  className={`flex-1 py-3 px-4 rounded-md font-medium transition-colors ${
                    plan.name?.includes('pro') || plan.name?.includes('enterprise')
                      ? 'bg-primary hover:bg-primary/80 text-white'
                      : 'bg-base-200 hover:bg-base-300 text-base-content'
                  }`}
                  onClick={() => alert(`This would redirect to subscription for ${plan.name}`)}
                >
                  {(typeof plan.price === 'number' ? plan.price : plan.price?.amount || 0) === 0
                    ? 'Get Started Free'
                    : 'Choose Plan'}
                </button>
              </div>

              <div className="flex space-x-2 mt-2">
                <button
                  className="flex-1 px-3 py-2 border border-base-300 rounded-md text-sm font-medium text-base-content bg-base-100 hover:bg-base-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  onClick={() => handleEditPlan(plan)}
                  title="Edit Plan"
                >
                  Edit
                </button>

                <button
                  className="flex-1 px-3 py-2 border border-base-300 rounded-md text-sm font-medium text-base-content bg-base-100 hover:bg-base-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  onClick={() => handleClonePlan(plan)}
                  title="Clone Plan"
                >
                  Clone
                </button>

                <button
                  className={`flex-1 px-3 py-2 border rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    planDeleteStatus[plan.id]?.canDelete
                      ? 'border-red-300 text-red-700 bg-base-100 hover:bg-red-50 focus:ring-red-500'
                      : 'border-base-300 text-base-content/50 bg-base-200 cursor-not-allowed'
                  } ${deleteLoading === plan.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                  onClick={() => handleDeletePlan(plan)}
                  disabled={!planDeleteStatus[plan.id]?.canDelete || deleteLoading === plan.id}
                  title={
                    planDeleteStatus[plan.id]?.canDelete
                      ? 'Delete Plan'
                      : planDeleteStatus[plan.id]?.reason || 'Cannot delete this plan'
                  }
                >
                  {deleteLoading === plan.id ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <TrashIcon className="h-4 w-4" />
                    </div>
                  )}
                </button>
              </div>

              <div className="mt-3 text-xs text-base-content/70 text-center space-y-1">
                <div>Plan ID: {plan.id}</div>
                {plan.createdAt && <div>Created: {new Date(plan.createdAt).toLocaleString()}</div>}
                <div className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Global Plan
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Table View for Global Plans
        <div className="bg-base-100 shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-base-300">
            <thead className="bg-base-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Limits
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Features
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-base-100 divide-y divide-base-300">
              {globalPlans.map((plan) => (
                <tr
                  key={plan.id}
                  id={`plan-${plan.id}`}
                  className={`transition-all duration-500 ${
                    highlightedPlanId === plan.id
                      ? 'bg-yellow-50 border-l-4 border-yellow-400'
                      : 'hover:bg-base-200'
                  }`}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <div className="text-sm font-medium text-base-content">
                        {plan.name || 'Unnamed Plan'}
                      </div>
                      <StripeIndicator
                        stripeProductId={plan.stripeProductId}
                        stripePriceId={plan.stripePriceId}
                      />
                    </div>
                    {plan.description && (
                      <div className="text-sm text-base-content/70 truncate max-w-xs">
                        {plan.description}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-base-content">
                      <div className="font-medium">
                        {getCurrencySymbol(
                          typeof plan.price === 'object' ? plan.price?.currency || 'GBP' : 'GBP'
                        )}
                        {typeof plan.price === 'number' ? plan.price : plan.price?.amount || 0}
                      </div>
                      <div className="text-base-content/70">
                        /
                        {plan.billingCycle ||
                          (typeof plan.price === 'object' ? plan.price.period : 'month')}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-base-content/70">
                    <div className="space-y-1">
                      <div>
                        Orgs:{' '}
                        {plan.limits?.organisations === 'unlimited'
                          ? '∞'
                          : plan.limits?.organisations || '1'}
                      </div>
                      <div>
                        Projects:{' '}
                        {plan.limits?.projects === 'unlimited' ? '∞' : plan.limits?.projects || '1'}
                      </div>
                      <div>
                        Streams:{' '}
                        {plan.limits?.streams === 'unlimited' ? '∞' : plan.limits?.streams || '1'}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-base-content/70">
                    {plan.features && Object.keys(plan.features).length > 0 ? (
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {Object.entries(plan.features)
                          .filter(([, enabled]) => enabled === true)
                          .slice(0, 2)
                          .map(([feature]) => (
                            <span
                              key={feature}
                              className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded"
                            >
                              {feature
                                .replace(/([A-Z])/g, ' $1')
                                .replace(/^./, (str) => str.toUpperCase())}
                            </span>
                          ))}
                        {Object.entries(plan.features).filter(([, enabled]) => enabled === true)
                          .length > 2 && (
                          <span className="text-xs text-base-content/50">
                            +
                            {Object.entries(plan.features).filter(([, enabled]) => enabled === true)
                              .length - 2}{' '}
                            more
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-base-content/50 text-xs">None</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Global Plan
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button
                        className="text-primary hover:text-primary/80"
                        onClick={() => handleEditPlan(plan)}
                        title="Edit Plan"
                      >
                        Edit
                      </button>
                      <button
                        className="text-green-600 hover:text-green-900"
                        onClick={() => handleClonePlan(plan)}
                        title="Clone Plan"
                      >
                        Clone
                      </button>
                      <button
                        className={`${
                          planDeleteStatus[plan.id]?.canDelete
                            ? 'text-red-600 hover:text-red-900'
                            : 'text-base-content/50 cursor-not-allowed'
                        } ${deleteLoading === plan.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                        onClick={() => handleDeletePlan(plan)}
                        disabled={
                          !planDeleteStatus[plan.id]?.canDelete || deleteLoading === plan.id
                        }
                        title={
                          planDeleteStatus[plan.id]?.canDelete
                            ? 'Delete Plan'
                            : planDeleteStatus[plan.id]?.reason || 'Cannot delete this plan'
                        }
                      >
                        {deleteLoading === plan.id ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Toasts */}
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />

      {/* Custom Plans Accordion */}
      {customPlans.length > 0 && (
        <div className="mt-6">
          <button
            onClick={() => setShowCustomPlans(!showCustomPlans)}
            className="w-full bg-base-200 px-4 py-3 rounded-md text-left font-semibold text-base-content hover:bg-base-300 transition-colors"
          >
            {showCustomPlans ? 'Hide' : 'Show'} Custom Plans ({customPlans.length})
          </button>

          {showCustomPlans && (
            <div className="mt-4 overflow-x-auto border border-base-300 rounded-md">
              <table className="min-w-full divide-y divide-base-300">
                <thead className="bg-base-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                      Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                      Organization
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-base-100 divide-y divide-base-300">
                  {customPlans.map((plan) => (
                    <tr
                      key={plan.id}
                      id={`plan-${plan.id}`}
                      className={`transition-all duration-500 ${
                        highlightedPlanId === plan.id
                          ? 'bg-yellow-50 border-l-4 border-yellow-400'
                          : 'hover:bg-base-200'
                      }`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-base-content">
                        <div className="flex items-center space-x-2">
                          <span>{plan.name}</span>
                          <StripeIndicator
                            stripeProductId={plan.stripeProductId}
                            stripePriceId={plan.stripePriceId}
                          />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-base-content/70">
                        {getCurrencySymbol(
                          typeof plan.price === 'object' ? plan.price?.currency || 'GBP' : 'GBP'
                        )}
                        {typeof plan.price === 'number' ? plan.price : plan.price?.amount || 0}
                        <span className="text-xs text-base-content/50">
                          /
                          {plan.billingCycle ||
                            (typeof plan.price === 'object' ? plan.price.period : 'month')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-base-content/70">
                        {plan.assignedOrgId ? (
                          `${plan.assignedOrgId} (${getOrganizationName(plan.assignedOrgId)})`
                        ) : (
                          <span className="text-base-content/50 italic">Unassigned</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-base-content/70">
                        {plan.createdAt ? new Date(plan.createdAt).toLocaleString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                        <button
                          className="text-primary hover:text-primary/80"
                          onClick={() => handleEditPlan(plan)}
                          title="Edit Plan"
                        >
                          Edit
                        </button>
                        <button
                          className="text-green-600 hover:text-green-900"
                          onClick={() => handleClonePlan(plan)}
                          title="Clone Plan"
                        >
                          Clone
                        </button>
                        <button
                          className={`${
                            planDeleteStatus[plan.id]?.canDelete
                              ? 'text-red-600 hover:text-red-900'
                              : 'text-base-content/50 cursor-not-allowed'
                          } ${deleteLoading === plan.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                          onClick={() => handleDeletePlan(plan)}
                          disabled={
                            !planDeleteStatus[plan.id]?.canDelete || deleteLoading === plan.id
                          }
                          title={
                            planDeleteStatus[plan.id]?.canDelete
                              ? 'Delete Plan'
                              : planDeleteStatus[plan.id]?.reason || 'Cannot delete plan'
                          }
                        >
                          {deleteLoading === plan.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {plans.length === 0 && (
        <div className="bg-base-200 rounded-lg p-8 text-center">
          <p className="text-base-content/70 mb-4">No pricing plans configured</p>
          <p className="text-sm text-base-content/70">
            Plans are configured in the backend pricing service
          </p>
        </div>
      )}

      {/* Toast Container */}
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </div>
  );
};

export default PricingPlans;
